'use client';

import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';

import { useCookieConsent } from '../../../contexts/CookieConsentContext';

import CookieBanner from './CookieBanner';

/**
 * Modal-ът се отваря само при явно действие — бутон „Настройки" в банера или
 * връзката в Политиката за поверителност. До тогава е мъртъв код в критичния
 * път: Lighthouse (2026-08-29) го отчете като 4030 от 4030 неизползвани байта в
 * layout chunk-а. Банерът остава статичен — той се показва при първо посещение.
 */
const CookieSettingsModal = dynamic(() => import('./CookieSettingsModal'), { ssr: false });

/**
 * Монтира банера (при първо посещение) и modal-а с настройки. Рендира се веднъж
 * в root layout-а. Настройките могат да се отворят и по-късно от бутона в
 * Политиката за поверителност (PrivacyPageClient.js) — това е входната точка
 * за оттегляне на съгласието.
 *
 * @param {boolean} hasTagManager - има ли GTM контейнер в този билд. Подава се от
 *   layout.js (сървърен компонент), а НЕ се чете от process.env тук: клиентският
 *   бъндъл инлайнва NEXT_PUBLIC_* при компилация и стойността мълчаливо липсва,
 *   ако dev сървърът е стартиран преди променливата да съществува — което скрива
 *   целия банер без никакво съобщение.
 */
export default function CookieConsent({ hasTagManager = true }) {
  const { ready, hasDecided, settingsOpen } = useCookieConsent();
  const pathname = usePathname();

  // Админ панелът е зад сесия и не е публичен — банерът няма работа там.
  // Публичният път е /admin (next.config пренаписва към /pages/admin), затова
  // проверяваме първо него; вторият префикс е defence-in-depth.
  const isAdminRoute = Boolean(
    pathname && (pathname.startsWith('/admin') || pathname.startsWith('/pages/admin')),
  );

  // Докато не прочетем localStorage — нищо (за да няма hydration mismatch).
  // Без GTM контейнер (напр. babyvogue) нито един таг не може да се зареди, така че
  // няма за какво да се иска съгласие.
  if (!ready || isAdminRoute || !hasTagManager) {
    return null;
  }

  return (
    <>
      {!hasDecided && !settingsOpen && <CookieBanner />}
      {settingsOpen && <CookieSettingsModal />}
    </>
  );
}
